import { useContext, useMemo } from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import { LanguageContext } from 'context/Language';
import { useTranslation } from 'hooks';
import { Article, PostalOfficeType } from 'types';
import { EmailMapLogo, HeaderCloseIcon } from 'resources';
import { pointItems } from 'utils';

const Icon = dynamic(() => import('components/Icon'));
const Link = dynamic(() => import('components/Link'));

const excludedOfficeType = [PostalOfficeType.DEPOSIT];

export interface MapProps<T> {
  lat?: T;
  lng?: T;
  office_type?: PostalOfficeType;
  postOffice?: Article;
  region?: string;
  city?: string;
  street?: string;
  block?: string;
  isOpen?: boolean;
  setMarkerId?: (markerId?: number) => void;
  finder?: boolean;
}

export const Marker: React.FC<MapProps<number> & { zip_code?: string }> = ({
  zip_code,
  office_type,
  region,
  city,
  block,
  street,
  isOpen,
  setMarkerId,
  finder,
}) => {
  const { t } = useTranslation(['common']);
  const { lang } = useContext(LanguageContext);

  const pinData = useMemo(
    () => (
      <div className="text-info">
        <p>{`${t(office_type)} ${zip_code || ''}`}</p>
        <p>{`${[region, city].filter((i) => i).join(' ')}`}</p>
        <p>{`${[street, block].filter((i) => i).join(' ')}`}</p>
      </div>
    ),
    [],
  );

  if (!pointItems.some(({ type }) => type === office_type)) return null;

  return (
    <div className={cn('map-pin', { finder })}>
      <div
        className={cn(`pin pin-${office_type}`)}
        onClick={() => (setMarkerId ? setMarkerId(isOpen ? null : undefined) : null)}
      ></div>
      {isOpen && (
        <div className="text-info-box">
          <div className="arrow" />
          <div className="text-info-box__logo">
            <Icon component={EmailMapLogo} />
          </div>
          {zip_code ? (
            <Link className="text-info-box__link" href={`/${lang}/map/${office_type}-${zip_code}`}>
              {pinData}
            </Link>
          ) : (
            <>{pinData}</>
          )}
          {setMarkerId ? (
            <Icon
              onClick={() => setMarkerId(null)}
              className="text-info-box__close"
              component={HeaderCloseIcon}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};
